import { useState, useEffect } from "react"
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Scale, Ruler, AlertCircle, Eye, Info, HeartPulse, Shield,
  Dog, CheckCircle2, XCircle, AlertTriangle, Dumbbell,
  Activity, Brain, Bone, Heart, Stethoscope, Droplets, MessageCircle, Phone, ArrowRight
} from 'lucide-react';
import { useAnalysis } from '../AnalysisContext';
import { SignedIn, SignedOut, UserButton, SignIn, useUser } from "@clerk/clerk-react";
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from "../components/ui/button";
import UpgradeModalVetConsult from '../pages/UpgradeModalVetConsult';
import HealthViewCard from './health/HealthViewCard';
import PricingModal from './PricingModal'
import { getUserPlan } from '@/utils/plans'

const checkPremiumStatus = (user) => {
  if (!user) return false;
  if (!user.unsafeMetadata) return false;
  return user.unsafeMetadata.isPremium === true;
};

// Reusable components remain the same as your original...
const HealthSection = ({ title, icon: Icon, children, variant = "default" }) => {
  const getVariantStyles = () => {
    switch (variant) {
      case "warning":
        return "bg-amber-50 border-amber-200";
      case "alert":
        return "bg-red-50 border-red-200";
      case "success":
        return "bg-emerald-50 border-emerald-200";
      default:
        return "bg-white";
    }
  };

  return (
    <Card className={`shadow-sm hover:shadow-md transition-all duration-200 ${getVariantStyles()}`}>
      <CardHeader className="border-b p-3 sm:p-4">
        <CardTitle className="text-base sm:text-base font-medium flex items-center gap-1.5 sm:gap-2">
          <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 sm:p-4">{children}</CardContent>
    </Card>
  );
};

const HealthIndicator = ({ title, value, icon: Icon, status = "normal", description }) => {
  const getStatusStyles = () => {
    switch (status) {
      case "good":
        return "bg-emerald-50 border-emerald-200 text-emerald-700";
      case "warning":
        return "bg-amber-50 border-amber-200 text-amber-700";
      case "alert":
        return "bg-red-50 border-red-200 text-red-700";
      default:
        return "bg-gray-50 border-gray-200 text-gray-700";
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case "good":
        return <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-500" />;
      case "warning":
        return <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-500" />;
      case "alert":
        return <XCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <div className={`p-3 sm:p-4 rounded-lg border ${getStatusStyles()}`}>
      <div className="flex items-start gap-2 sm:gap-3">
        <div className="p-1.5 sm:p-2 bg-white rounded-lg shadow-sm">
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <h3 className="font-medium text-sm sm:text-sm truncate">{title}</h3>
            {getStatusIcon()}
          </div>
          <div className="text-lg sm:text-lg font-bold mt-0.5 sm:mt-1">{value}</div>
          {description && (
            <p className="text-sm sm:text-sm mt-0.5 sm:mt-1 opacity-90">{description}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const RecommendationList = ({ recommendations }) => (
  <div className="space-y-1.5 sm:space-y-2">
    {recommendations.map((rec, idx) => (
      <div key={idx} className="flex items-start gap-1.5 sm:gap-2 text-sm sm:text-sm text-gray-600">
        <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 mt-0.5 text-emerald-500" />
        <span className="break-words">{rec}</span>
      </div>
    ))}
  </div>
);

const HealthTab = () => {
  const { analysisData } = useAnalysis();
  const navigate = useNavigate();
  const { user, isLoaded, isSignedIn } = useUser();
  const isPremium = checkPremiumStatus(user);
  const [showUpgradeModalVetConsult, setShowUpgradeModalVetConsult] = useState(false);
  const plan = isLoaded ? getUserPlan(user) : null;

  async function onSubmit(event) {
    event.preventDefault();

    if (!isPremium) {
      setShowUpgradeModalVetConsult(true);
      return;
    }

    const message = encodeURIComponent(
      "Hola, me gustaría agendar una consulta veterinaria."
    );
    window.open(`https://wa.me/525643251394?text=${message}`, '_blank');
  }

  if (!analysisData) {
    return (
      <Card className="max-w-md mx-auto mt-8 bg-gradient-to-br from-orange-50 to-amber-50 shadow-lg border-none">
        <CardContent className="p-8">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <div className="bg-orange-100 p-4 rounded-full">
                <HeartPulse className="h-8 w-8 text-orange-500" />
                <motion.div
                  className="absolute -bottom-1 -right-1 h-3 w-3 bg-green-400 rounded-full"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </div>
            </div>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="absolute"
            >
              <div className="h-16 w-16 rounded-full border-4 border-orange-200 border-t-orange-500" />
            </motion.div>
            <div className="text-center space-y-2 mt-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Analizando el estado de salud
              </h3>
              <p className="text-sm text-gray-600">
                Procesando los indicadores de bienestar de tu mascota
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const {
    analisisGeneral: {
      healthView: {
      } = {}
    } = {}
  } = analysisData || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-6xl mx-auto px-2 sm:px-4 py-3 sm:py-6 space-y-4 sm:space-y-6"
    >
      {/* Consultation prompt */}
      <Alert className="bg-orange-50 border-orange-200">
        <Stethoscope className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-500" />
        <AlertDescription className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
          <span className="text-orange-700 text-sm sm:text-sm">
            Revisa estos resultados con un veterinario en línea
          </span>
          <Button
            onClick={onSubmit}
            className="flex items-center justify-center gap-1.5 sm:gap-2 bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto"
            size="sm"
          >
            <span className="text-sm sm:text-sm">Agendar Revisión</span>
            <MessageCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </Button>
        </AlertDescription>
      </Alert>
      <HealthViewCard healthView={analysisData.analisisGeneral.healthView}/>

      {/* Disclaimer */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-500" />
        <AlertDescription className="text-sm sm:text-sm text-blue-700">
          Este análisis está basado en observaciones visuales y datos proporcionados.
          Para una evaluación más completa, te recomendamos consultar con tu veterinario.
        </AlertDescription>
      </Alert>

      <PricingModal
            isOpen={showUpgradeModalVetConsult}
            onClose={() => setShowUpgradeModalVetConsult(false)}
            plan={plan} 
          />
    </motion.div>
  );
};

export default HealthTab;


